import {
  Box,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  FileFolderSelectionProvider,
  useFileFolderSelection,
} from '../../../components/Providers/FileFolderSelectionProvider';
import { useFetchData } from '../../../hooks/useFetchData';
import { selectFolders } from '../../../redux/reducers/folderReducer';
import { updateKpisForRun } from '../../../services/dealGPT';
import { KpiMetric, ProjectWithLastScanned } from '../../../Types/dealGPT';
import { projectSemaRunPathUrl } from '../../../util/routesUrls';
import EditAndConfirmDataStep from './EditAndConfirmDataStep';
import SelectSemaScanTypeStep from './SelectSemaScanTypeStep';
import ValidateSemaFilesStep from './ValidateSemaFilesStep';

const steps: readonly string[] = [
  'SEMA 2-Step or SEMA Full',
  'Process Data',
  'Edit & Confirm Data',
];

interface SemaProcessingProps {
  project: ProjectWithLastScanned;
}

const AlignedConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
    left: 'calc(-50% + 6px)',
    width: '90%',
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey[300],
    borderTopWidth: 2,
  },
}));

const SemaProcessingContent = ({ project }: SemaProcessingProps): JSX.Element => {
  const { currentFolder } = useSelector(selectFolders);
  const [activeStep, setActiveStep] = useState(0);
  const { clearSelection } = useFileFolderSelection();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingExistingRun, setIsEditingExistingRun] = useState(false);
  const {
    data: runId,
    refetch: refetchRun,
    loading: isLoadingInitialRun,
  } = useFetchData<string | null>({
    urlPath: projectSemaRunPathUrl(project.id),
  });

  useEffect(() => {
    if (!runId || runId === '') return;
    setActiveStep(2);
    setIsEditingExistingRun(true);
  }, [runId]);

  const goToNextStep = useCallback(() => {
    setActiveStep((prev) => prev + 1);
  }, []);

  const handleClearAndGoToStep1 = useCallback(() => {
    clearSelection();
    setIsEditingExistingRun(false);
    setActiveStep(0);
  }, [clearSelection]);

  const handleProcessComplete = useCallback(() => {
    goToNextStep();
  }, [goToNextStep]);

  const handleSave = useCallback(
    async (edited: KpiMetric[], selectedRepos: string[]) => {
      try {
        setIsLoading(true);
        const finalPayload: KpiMetric[] = edited.map((metric) => {
          const detail = metric.detail
            ? {
                repositories: metric.detail.repositories.map((repo) => ({
                  name: repo.name,
                  value: repo.value,
                  selected: selectedRepos.includes(repo.name),
                })),
              }
            : undefined;

          return {
            id: metric.id,
            name: metric.name,
            value: metric.value,
            detail,
          };
        });

        await updateKpisForRun(project.id, runId ?? '', finalPayload);
        enqueueSnackbar('KPIs saved successfully!', { variant: 'success' });
      } catch (error) {
        console.error('Failed to save KPIs:', error);
        enqueueSnackbar('Failed to save KPIs. Please try again.', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [project.id, runId]
  );

  const renderStep = useCallback((): JSX.Element => {
    switch (activeStep) {
      case 0:
        return (
          <SelectSemaScanTypeStep
            vectorStoreId={currentFolder?.indexIds?.[0] ?? ''}
            onNext={goToNextStep}
          />
        );
      case 1:
        return (
          <ValidateSemaFilesStep
            projectId={project.id}
            onClear={handleClearAndGoToStep1}
            onProcess={handleProcessComplete}
            refetchRun={refetchRun}
          />
        );
      case 2:
        return (
          <EditAndConfirmDataStep
            onCancel={handleClearAndGoToStep1}
            onSave={handleSave}
            isLoading={isLoading}
            isEditingExistingRun={isEditingExistingRun}
            projectId={project.id}
            run={runId}
          />
        );
      default:
        return <></>;
    }
  }, [
    activeStep,
    isLoading,
    goToNextStep,
    handleClearAndGoToStep1,
    currentFolder?.indexIds,
    handleProcessComplete,
    project.id,
    handleSave,
    isEditingExistingRun,
    runId,
    refetchRun,
  ]);

  if (isLoadingInitialRun) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Data Processing
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel connector={<AlignedConnector />}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{ sx: { mb: 1 } }}
              sx={{
                textAlign: 'center',
                typography: 'body2',
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Step {index + 1}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {label}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box>{renderStep()}</Box>
    </Box>
  );
};

const SemaProcessing = ({ project }: SemaProcessingProps): JSX.Element => {
  return (
    <FileFolderSelectionProvider>
      <SemaProcessingContent project={project} />
    </FileFolderSelectionProvider>
  );
};

export default SemaProcessing;
